/**
 * Settings' Bank Files (plan §G.5, G2): importing a file downloaded from a
 * bank this application has no feed for. Each transaction becomes a draft to
 * categorize, exactly as a Mercury transaction arrives.
 *
 * NOTHING IS WRITTEN UNTIL THE PREVIEW HAS BEEN SEEN. Choosing the account
 * and the file, and for a CSV the columns, asks the server what an import
 * would do: how many transactions, over which dates, how many it already
 * holds, and the first rows as it read them, so a wrong column shows before
 * anything is written. Import sends the same file again; the server reads it
 * afresh, so what is written is what the file says, never a stale preview.
 *
 * THE FILE IS READ IN THE BROWSER AND SENT AS TEXT. It is never stored: the
 * transactions are, with the file's name and fingerprint (0068).
 *
 * A CSV NAMES NO COLUMNS THE SAME WAY TWICE, so the operator says which is
 * the date, the description and the amount. The header row's names are
 * offered, and a first guess from them is filled in; the preview is what
 * proves the guess. OFX and QFX carry their own structure and need none of it.
 *
 * Read on its own, as Bank Connections is, so a failure here leaves the rest
 * of Settings working.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  ApiError,
  api,
  type BankImportPreview,
  type BankImportSummary,
  type CsvMapping,
  type ReconcilableAccount,
} from '../api.js';
import { columnNames, firstCsvRows, guessColumns } from '../lib/csv-columns.js';
import { formatCents } from '../lib/money.js';
import { formatTimestamp } from '../lib/timezone.js';
import { FileDropzone } from './FileDropzone.js';
import { InfoNote } from './InfoNote.js';
import { Toast } from './Toast.js';

/** The server's own cap is 2 MB of text; said here before a large file is sent. */
const MAX_CHARS = 2 * 1024 * 1024;

interface PickedFile {
  name: string;
  text: string;
  isCsv: boolean;
}

interface MappingForm {
  hasHeaderRow: boolean;
  dateColumn: string;
  dateFormat: CsvMapping['dateFormat'];
  descriptionColumn: string;
  amountKind: 'signed' | 'split';
  amountColumn: string;
  outColumn: string;
  inColumn: string;
  referenceColumn: string;
}

/**
 * The file's bytes as text (review BK-1). UTF-8 when they are valid UTF-8;
 * otherwise Windows-1252, which is what an OFX 1.x file saying `CHARSET:1252`
 * and most bank CSVs from Windows software are. Reading such a file as UTF-8
 * turns "CAFÉ" into "CAF�", and a posted entry keeps its payee forever.
 * A wrong guess the other way cannot happen: Windows-1252 text with any
 * accented letter is almost never valid UTF-8, and plain ASCII reads the
 * same in both.
 */
function decodeBankFile(bytes: ArrayBuffer): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

function message(caught: unknown, fallback: string): string {
  return caught instanceof ApiError && caught.message !== caught.code ? caught.message : fallback;
}

function initialMapping(text: string): MappingForm {
  const header = firstCsvRows(text, 1)[0] ?? [];
  const guess = guessColumns(header);
  const at = (index: number | null) => (index === null ? '' : String(index));
  const split = guess.amount === null && guess.out !== null && guess.into !== null;
  return {
    hasHeaderRow: true,
    dateColumn: at(guess.date),
    dateFormat: 'MM/DD/YYYY',
    descriptionColumn: at(guess.description),
    amountKind: split ? 'split' : 'signed',
    amountColumn: at(guess.amount),
    outColumn: at(guess.out),
    inColumn: at(guess.into),
    referenceColumn: '',
  };
}

/** The mapping as the API takes it, or a sentence saying what is missing. */
function toMapping(form: MappingForm): CsvMapping | string {
  const column = (value: string) => (value === '' ? null : Number(value));
  const date = column(form.dateColumn);
  const description = column(form.descriptionColumn);
  if (date === null) return 'Choose the column that holds the date.';
  if (description === null) return 'Choose the column that holds the description.';
  let amount: CsvMapping['amount'];
  if (form.amountKind === 'signed') {
    const only = column(form.amountColumn);
    if (only === null) return 'Choose the column that holds the amount.';
    amount = { kind: 'signed', column: only };
  } else {
    const out = column(form.outColumn);
    const into = column(form.inColumn);
    if (out === null || into === null) return 'Choose both the money-out and the money-in columns.';
    amount = { kind: 'split', outColumn: out, inColumn: into };
  }
  return {
    hasHeaderRow: form.hasHeaderRow,
    dateColumn: date,
    dateFormat: form.dateFormat,
    descriptionColumn: description,
    amount,
    referenceColumn: column(form.referenceColumn),
  };
}

export function BankImportSection() {
  const [imports, setImports] = useState<BankImportSummary[] | null>(null);
  const [accounts, setAccounts] = useState<ReconcilableAccount[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [file, setFile] = useState<PickedFile | null>(null);
  const [mapping, setMapping] = useState<MappingForm | null>(null);
  const [preview, setPreview] = useState<BankImportPreview | null>(null);

  async function load() {
    try {
      const [listed, banks] = await Promise.all([api.listBankImports(), api.listReconcilableAccounts()]);
      setImports(listed.imports);
      setAccounts(banks.accounts);
      setLoadError(null);
    } catch (caught) {
      setLoadError(message(caught, 'Could not read the imported bank files.'));
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const csvRows = useMemo(() => (file?.isCsv ? firstCsvRows(file.text) : []), [file]);
  const names = useMemo(() => columnNames(csvRows, mapping?.hasHeaderRow ?? true), [csvRows, mapping?.hasHeaderRow]);

  function reset() {
    setOpen(false);
    setAccountId('');
    setFile(null);
    setMapping(null);
    setPreview(null);
    setError(null);
  }

  async function pick(files: File[]) {
    const chosen = files[0];
    if (!chosen) return;
    setError(null);
    setPreview(null);
    const isCsv = /\.csv$/i.test(chosen.name);
    if (!/\.(ofx|qfx|csv)$/i.test(chosen.name)) {
      setFile(null);
      setError('Choose an .ofx, .qfx or .csv file. Most banks offer one of these under Download or Export.');
      return;
    }
    const text = decodeBankFile(await chosen.arrayBuffer());
    if (text.length > MAX_CHARS) {
      setFile(null);
      setError('The file is larger than 2 MB. Download a shorter date range and import it in parts.');
      return;
    }
    setFile({ name: chosen.name, text, isCsv });
    setMapping(isCsv ? initialMapping(text) : null);
  }

  function input() {
    if (accountId === '') return 'Choose the bank account this file is for.';
    if (!file) return 'Choose the file to import.';
    let csvMapping: CsvMapping | null = null;
    if (file.isCsv) {
      const mapped = mapping ? toMapping(mapping) : 'Say which columns hold what.';
      if (typeof mapped === 'string') return mapped;
      csvMapping = mapped;
    }
    return { accountId, fileName: file.name, text: file.text, csvMapping };
  }

  async function submitPreview() {
    const body = input();
    if (typeof body === 'string') {
      setError(body);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setPreview(await api.previewBankImport(body));
    } catch (caught) {
      setPreview(null);
      setError(message(caught, 'Could not read the file.'));
    } finally {
      setBusy(false);
    }
  }

  async function submitImport() {
    const body = input();
    if (typeof body === 'string') {
      setError(body);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.importBankFile(body);
      reset();
      setNotice(
        `Imported ${result.rowsCreated} new ${result.rowsCreated === 1 ? 'transaction' : 'transactions'}` +
          (result.rowsSkipped > 0 ? `; ${result.rowsSkipped} already held were skipped.` : '.') +
          (result.rowsCreated > 0 ? ' They are waiting in Transactions to be categorized.' : ''),
      );
      await load();
    } catch (caught) {
      setError(message(caught, 'Could not import the file.'));
    } finally {
      setBusy(false);
    }
  }

  function changeMapping(change: Partial<MappingForm>) {
    setPreview(null);
    setMapping((current) => (current ? { ...current, ...change } : current));
  }

  const columnSelect = (label: string, key: keyof MappingForm, optional = false) => (
    <label className="field">
      <span>{label}</span>
      <select
        value={String(mapping?.[key] ?? '')}
        onChange={(event) => {
          const value = event.target.value;
          changeMapping({ [key]: value } as Partial<MappingForm>);
        }}
      >
        <option value="">{optional ? 'None' : 'Choose…'}</option>
        {names.map((name, index) => (
          <option key={name} value={String(index)}>
            {name}
          </option>
        ))}
      </select>
    </label>
  );

  const toImport = preview ? preview.transactionCount - preview.alreadyHeld : 0;

  return (
    <section className="settings-section" aria-labelledby="settings-bank-files">
      <h2 id="settings-bank-files">Bank Files</h2>
      <p className="muted">
        For a bank this application has no feed for. Each transaction in the file becomes a draft to categorize, and one
        already imported is skipped.{' '}
        <InfoNote label="Which File To Download">
          In the bank&apos;s website, look under Download, Export or Statements for a file named OFX, QFX, Quicken or
          Money; any of those is the right one, and it says which account it is for. If the bank offers only CSV
          (spreadsheet), choose that, and say which column holds the date, the description and the amount. A CSV
          rarely carries the bank&apos;s own id for each transaction, so two CSV downloads whose dates overlap can, for
          two identical purchases on the same day, import one of them twice. Keep to one kind of file for each account:
          the same transaction from an OFX and from a CSV is not recognised as the same.
        </InfoNote>
      </p>

      {notice && !error && <Toast message={notice} variant="ok" onDismiss={() => setNotice(null)} />}
      {loadError && (
        <p role="alert" className="error">
          {loadError}
        </p>
      )}

      {imports !== null && imports.length > 0 && (
        <div className="table-scroll">
          <table className="lines">
            <thead>
              <tr>
                <th>Imported</th>
                <th>Account</th>
                <th>File</th>
                <th className="num">Read</th>
                <th className="num">New</th>
                <th className="num">Already Held</th>
              </tr>
            </thead>
            <tbody>
              {imports.map((row) => (
                <tr key={row.id}>
                  <td>{formatTimestamp(row.importedAt)}</td>
                  <td>
                    {row.accountCode} {row.accountName}
                  </td>
                  <td>{row.fileName}</td>
                  <td className="num">{row.rowsRead}</td>
                  <td className="num">{row.rowsCreated}</td>
                  <td className="num">{row.rowsSkipped}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {imports !== null && imports.length === 0 && <p className="muted">No bank file has been imported.</p>}

      {!open && imports !== null && (
        <div className="row">
          <button
            type="button"
            onClick={() => {
              reset();
              setNotice(null);
              setOpen(true);
            }}
          >
            Import A Bank File
          </button>
        </div>
      )}

      {open && (
        <div className="new-account" role="group" aria-label="Import A Bank File">
          {accounts.length === 0 ? (
            <p className="muted">
              This business has no bank account in its chart. Give an account the bank role in Chart of Accounts first.
            </p>
          ) : (
            <label className="field">
              <span>Bank Account</span>
              <select
                value={accountId}
                onChange={(event) => {
                  const value = event.target.value;
                  setAccountId(value);
                  setPreview(null);
                }}
              >
                <option value="">Choose…</option>
                {accounts.map((account) => (
                  <option key={account.accountId} value={account.accountId}>
                    {account.code} {account.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          <FileDropzone
            upload={pick}
            onError={setError}
            inputLabel="Bank File"
            idleText={file ? `${file.name} — choose another to replace it` : 'Drop an OFX, QFX or CSV file, or click to choose one'}
            accept=".ofx,.qfx,.csv,text/csv,application/x-ofx"
            busyText="Reading…"
            failureText="Could not read that file."
            compact
          />

          {file?.isCsv && mapping && (
            <fieldset className="csv-mapping">
              <legend>Which Column Holds What</legend>
              <label className="toggle-line">
                <input
                  type="checkbox"
                  checked={mapping.hasHeaderRow}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    changeMapping({ hasHeaderRow: checked });
                  }}
                />
                <span>The First Row Names The Columns</span>
              </label>
              {columnSelect('Date', 'dateColumn')}
              <label className="field">
                <span>Date Format</span>
                <select
                  value={mapping.dateFormat}
                  onChange={(event) => {
                    const value = event.target.value as CsvMapping['dateFormat'];
                    changeMapping({ dateFormat: value });
                  }}
                >
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </label>
              {columnSelect('Description', 'descriptionColumn')}
              <label className="field">
                <span>Amounts</span>
                <select
                  value={mapping.amountKind}
                  onChange={(event) => {
                    const value = event.target.value as MappingForm['amountKind'];
                    changeMapping({ amountKind: value });
                  }}
                >
                  <option value="signed">One Signed Column</option>
                  <option value="split">Separate Out And In Columns</option>
                </select>
              </label>
              {mapping.amountKind === 'signed' ? (
                columnSelect('Amount', 'amountColumn')
              ) : (
                <>
                  {columnSelect('Money Out', 'outColumn')}
                  {columnSelect('Money In', 'inColumn')}
                </>
              )}
              {columnSelect('Bank Reference', 'referenceColumn', true)}
            </fieldset>
          )}

          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}

          {preview && (
            <div className="bank-import-preview" aria-label="What Importing Would Do">
              <p>
                {preview.transactionCount} {preview.transactionCount === 1 ? 'transaction' : 'transactions'} from{' '}
                {preview.firstDate} to {preview.lastDate}: {formatCents(preview.moneyInCents)} in,{' '}
                {formatCents(preview.moneyOutCents)} out.{' '}
                {preview.alreadyHeld > 0
                  ? `${preview.alreadyHeld} ${preview.alreadyHeld === 1 ? 'is' : 'are'} already held and would be skipped.`
                  : 'None is held yet.'}
                {preview.insideClosedBooks > 0 &&
                  ` ${preview.insideClosedBooks} ${preview.insideClosedBooks === 1 ? 'is' : 'are'} dated inside the closed books, and would need a date in an open period before being posted.`}
              </p>
              {preview.warnings.map((warning) => (
                <p key={warning.code} className="notice notice-warn">
                  {warning.message}
                </p>
              ))}
              <p className="muted">The first rows, as read:</p>
              <div className="table-scroll">
                <table className="lines" aria-label="The first rows, as read">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Memo</th>
                      <th className="num">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample.map((row) => (
                      <tr key={row.ref}>
                        <td>{row.date}</td>
                        <td>{row.counterparty ?? ''}</td>
                        <td>{row.memo ?? ''}</td>
                        <td className="num">{formatCents(row.amountCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="row">
            {preview ? (
              <button type="button" disabled={busy} onClick={() => void submitImport()}>
                {busy
                  ? 'Importing…'
                  : toImport === 0
                    ? 'Import Anyway'
                    : `Import ${toImport} ${toImport === 1 ? 'Transaction' : 'Transactions'}`}
              </button>
            ) : (
              <button type="button" disabled={busy} onClick={() => void submitPreview()}>
                {busy ? 'Reading…' : 'Preview'}
              </button>
            )}
            <button type="button" className="link" onClick={reset}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
